import { FC, memo, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { isPolygonPredicateType } from "constants/filter.constants";
import _debounce from "lodash/debounce";
import {
  removeExcludePolygon as removeFacilityExcludePolygon,
  removeProperty as removeFacilityProperty,
  removeWellListFilter as removeFacilityWellListFilter,
  setMapViewFilterDisabled as setFacilityMapViewFilterDisabled
} from "store/features/filter/facilityFilterSlice";
import {
  removeExcludePolygon,
  removeProperty,
  removeWellListFilter,
  setMapViewFilterDisabled
} from "store/features/filter/filterSlice";
import { hasWellListFilter } from "store/features/filter/utils/hasWellListFilter";
import { RootState } from "store/rootReducer";
import { useMapStore } from "stores/map";
import styled from "styled-components";
import { queryToPredicates } from "utils/filter";

import { EntityKind } from "../../models/entityKind";
import PredicateTree from "./PredicateTree";
import { usePropertyTitleLookup, useShapefileBufferLabelUnit } from "./hooks";
import { ShapefileBufferLabelUnit } from "./util/shapefileFilterPredicate";

const emptyPredicate = {
  id: "",
  title: "",
  name: "",
  children: [],
  expanded: false,
  operator: "",
  type: "",
  isChecked: false
};

type ActiveFilterProps = {
  className?: string;
  onNodeRemoved?: (node) => void;
  onMouseDoubleClick?: (node) => void;
  entityKind: EntityKind;
};

const ActiveFilter: FC<ActiveFilterProps> = ({
  className = "",
  onNodeRemoved,
  onMouseDoubleClick,
  entityKind = EntityKind.Well
}) => {
  const dispatch = useDispatch();
  const setViewLock = useMapStore((s) => s.setViewLock);
  const [predicates, setPredicates] = useState([emptyPredicate]);

  // state from store
  const filterCounts = useSelector((state: RootState) =>
    entityKind == EntityKind.Well
      ? state.filter.filterCounts
      : state.facilityFilter.filterCounts
  );
  const currentFilter = useSelector((state: RootState) =>
    entityKind == EntityKind.Well
      ? state.filter.currentFilter
      : state.facilityFilter.currentFilter
  );
  const propertyTitleLookup = usePropertyTitleLookup();
  // A shapefile buffer is one distance in metres, said in the unit this reader
  // works in — so the chip changes wording with the unit setting, though the
  // filter it describes does not change at all.
  const bufferLabelUnit = useShapefileBufferLabelUnit();

  // Removing the map-view node only means "hold this list whole" while there
  // is a list to hold. With no list it keeps its old meaning, which is to
  // clear the filter.
  const wellListFilter = useSelector((state: RootState) =>
    entityKind == EntityKind.Well
      ? state.filter.wellListFilter
      : state.facilityFilter.wellListFilter
  );
  const hasWellList = hasWellListFilter(wellListFilter);

  const isViewLocked = useMapStore((s) => s.viewLock);
  const isViewLockedRef = useRef(isViewLocked);
  // PredicateTree captures onDeleteNode once, at mount, so the handler below
  // has to read this the same way it reads the view lock.
  const hasWellListRef = useRef(hasWellList);

  const debouncedUpdatePredicates = useRef(
    _debounce(
      (
        filter,
        counts: Set<number>,
        lookup: Maybe<Map<string, string>>,
        labelUnit: ShapefileBufferLabelUnit
      ) => {
        if (!filter) return;

        const nextPredicates = queryToPredicates(
          filter.query,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          counts as any,
          lookup,
          labelUnit
        );
        setPredicates([nextPredicates]);
      },
      500
    )
  ).current;

  useEffect(() => {
    if (!currentFilter) return;

    debouncedUpdatePredicates(
      currentFilter,
      filterCounts,
      propertyTitleLookup,
      bufferLabelUnit
    );
    return () => {
      debouncedUpdatePredicates.cancel();
    };
  }, [
    currentFilter,
    filterCounts,
    propertyTitleLookup,
    bufferLabelUnit,
    debouncedUpdatePredicates
  ]);

  useEffect(() => {
    isViewLockedRef.current = isViewLocked;
  }, [isViewLocked]);

  useEffect(() => {
    hasWellListRef.current = hasWellList;
  }, [hasWellList]);

  const onDeleteNode = useCallback(
    (node) => {
      if (!node?.title) return;

      const actual = node.actualValue;
      if (node.title.startsWith("Well List")) {
        if (entityKind == EntityKind.Facility) {
          dispatch(removeFacilityWellListFilter(actual));
        } else {
          dispatch(removeWellListFilter(actual));
        }
        return;
      }

      // A branch, so every predicate under it goes out at once. This is the
      // "clear the filter" gesture and it keeps working the way it always
      // has: each child is handed to onNodeRemoved, and the map-view child
      // among them means the same thing there as it did before — clear
      // everything. Turning the viewport off is a single-node act, handled
      // below, and must not happen from here.
      if (node.children.length) {
        node.children.forEach((ch) => {
          if (entityKind == EntityKind.Facility) {
            dispatch(removeFacilityProperty(ch.actualValue));
          } else {
            dispatch(removeProperty(ch.actualValue));
          }
          if (onNodeRemoved) {
            onNodeRemoved(ch.actualValue);
          }
        });
        return;
      }

      const isPolygonType = isPolygonPredicateType(actual.type);
      if (isPolygonType && actual.operator === "!=") {
        // Remove the exclude well polygon predicate.
        if (entityKind == EntityKind.Facility) {
          dispatch(removeFacilityExcludePolygon(actual));
        } else {
          dispatch(removeExcludePolygon(actual));
        }
      } else if (
        isPolygonType &&
        actual.source !== "selection" &&
        hasWellListRef.current
      ) {
        // The map view, with a list under it. Removing it turns the viewport
        // off for that list; applying a list again turns it back on. It stops
        // here on purpose: onNodeRemoved clears every property filter and
        // takes the well list with it, which is not what removing one node
        // means. With no list the branch below still runs, and removing this
        // node clears the filter the way it always did.
        //
        // An older saved filter can hold a lasso with no source of its own,
        // which reads as the map view and arrives here with the view locked.
        // Release the lock, the same as removing any other polygon does.
        if (isViewLockedRef.current) {
          setViewLock(false);
        }
        dispatch(
          entityKind == EntityKind.Facility
            ? setFacilityMapViewFilterDisabled(true)
            : setMapViewFilterDisabled(true)
        );
        return;
      } else if (actual.property !== "Polygon") {
        if (entityKind == EntityKind.Facility) {
          dispatch(removeFacilityProperty(actual));
        } else {
          // when syncing with nearby wells, removing the selection filter
          // needs to unlock the map view lock
          if (isPolygonType && isViewLockedRef.current) {
            setViewLock(false);
          }
          dispatch(removeProperty(actual));
        }
        if (onNodeRemoved) {
          onNodeRemoved(actual);
        }
      }
    },
    [dispatch, onNodeRemoved]
  );

  return (
    <Wrapper className={className}>
      <PredicateTree
        entityKind={entityKind}
        predicates={predicates}
        onMouseDoubleClick={onMouseDoubleClick}
        onDeleteNode={onDeleteNode}
      />
    </Wrapper>
  );
};

export default memo(ActiveFilter);

const Wrapper = styled.div`
  margin: 0;
  overflow-y: auto;
`;
